import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { isColorDark } from './public/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = {
  mentions_legales: 'Mentions Legales',
  politique_confidentialite: 'Politique de Confidentialite',
  cgv: 'Conditions Generales de Vente'
};

const PublicLegal = () => {
  const { slug, pageType } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLegal = async () => {
      try {
        const response = await axios.get(`${API}/public/shops/${slug}/legal/${pageType}`);
        setData(response.data);
      } catch (error) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLegal();
  }, [slug, pageType]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="w-12 h-12 rounded-full border-4 border-zinc-800 border-t-amber-500 animate-spin" />
      </div>
    );
  }

  if (!data || !data.content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-white mb-3">Page non disponible</h1>
          <p className="text-zinc-500 mb-6">Ce document n'est pas encore disponible.</p>
          <Button onClick={() => navigate(`/s/${slug}`)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-black">
            Retour au catalogue
          </Button>
        </div>
      </div>
    );
  }

  const pageBg = data.backgroundColor || '#09090B';
  const textColor = data.textColor || '#FAFAFA';
  const headerColor = data.headerColor || data.accentColor || '#F59E0B';
  const isDark = isColorDark(pageBg);

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBg, color: textColor }} data-testid="public-legal-page">
      {/* Header */}
      <header style={{ backgroundColor: headerColor }}>
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/s/${slug}`)}
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors hover:bg-black/10"
              style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}
              data-testid="legal-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            {data.logoUrl && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${data.logoUrl}`} alt={data.shopName} className="w-10 h-10 rounded-lg object-cover" />
            )}
            <h1 className="text-lg font-bold" style={{ color: isColorDark(headerColor) ? '#fff' : '#000' }}>
              {data.shopName}
            </h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-6" data-testid="legal-page-title">{TITLES[pageType] || pageType}</h2>
        <div
          className="prose prose-sm max-w-none leading-relaxed"
          style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }}
          data-testid="legal-page-content"
        >
          {data.content.split('\n').map((line, i) => {
            if (line.startsWith('### ')) {
              return <h3 key={i} className="text-lg font-semibold mt-6 mb-2" style={{ color: textColor }}>{line.replace('### ', '')}</h3>;
            }
            if (line.startsWith('## ')) {
              return <h2 key={i} className="text-xl font-bold mt-8 mb-3" style={{ color: textColor }}>{line.replace('## ', '')}</h2>;
            }
            if (line.startsWith('# ')) {
              return <h1 key={i} className="text-2xl font-bold mt-8 mb-4" style={{ color: textColor }}>{line.replace('# ', '')}</h1>;
            }
            if (line.startsWith('- ')) {
              return <li key={i} className="ml-4 mb-1">{line.replace('- ', '')}</li>;
            }
            if (line.startsWith('**') && line.endsWith('**')) {
              return <p key={i} className="font-semibold mt-4 mb-1">{line.replace(/\*\*/g, '')}</p>;
            }
            if (line.trim() === '') return <br key={i} />;
            return <p key={i} className="mb-2">{line}</p>;
          })}
        </div>
      </main>
    </div>
  );
};

export default PublicLegal;
